import type { FC } from 'react';
import React, { useState, useCallback } from 'react';
import { cssMap } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';

import { token } from '@atlaskit/tokens';
import EditIcon from '@atlaskit/icon/core/edit';
import Tooltip from '@atlaskit/tooltip';

import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context.ts';
import { LazyEmojiComponentLoader } from '@confluence/emoji-title/entry-points/EmojiComponent.tsx';
import { EMOJI_SIZE_LARGE } from '@confluence/emoji-title/entry-points/emojiTitleConstants.ts';
import { EditSpaceLogo } from '@confluence/edit-space-logo';
import type { EmojiDescription } from '@atlaskit/emoji/types';

const styles = cssMap({
	spaceIcon: {
		borderRadius: token('radius.large'),
		display: 'block',
	},
	editIconOverlay: {
		position: 'absolute',
		height: '100%',
		width: '100%',
		top: token('space.0'),
		left: token('space.0'),
		opacity: 0,
		transition: 'opacity 0.2s ease',
		backgroundColor: token('color.blanket'),
		borderRadius: token('radius.large'),
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	editIconOverlayVisible: {
		opacity: 1,
	},
	spaceIconContainer: {
		cursor: 'pointer',
		padding: token('space.0'),
		border: 'none',
		background: 'none',
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	iconWrapper: {
		position: 'relative',
		display: 'inline-flex',
	},
	logoRoot: {
		display: 'inline-flex',
	},
});

const i18n = defineMessages({
	editSpaceLogoTooltip: {
		id: 'space-overview.smart-space-overview.logo.edit-space-logo-tooltip',
		defaultMessage: 'Edit space logo',
		description: 'Tooltip for the space logo/icon that allows admins to edit space logo',
	},
});

type SmartSpaceOverviewLogoProps = {
	spaceKey: string;
	isAdmin: boolean;
	isPersonalSpace: boolean;
	emoji: string | EmojiDescription | null;
	spaceIconPath: string | undefined;
};

export const SmartSpaceOverviewLogo: FC<SmartSpaceOverviewLogoProps> = ({
	spaceKey,
	isAdmin,
	isPersonalSpace,
	emoji,
	spaceIconPath,
}) => {
	const intl = useIntl();
	const [isOpen, setIsOpen] = useState(false);
	const [shouldRenderEditSpaceLogoDialog, setShouldRenderEditSpaceLogoDialog] = useState(false);
	const [isHovered, setIsHovered] = useState(false);

	const onOpen = useCallback(() => setIsOpen(true), []);
	const onClose = useCallback(() => setIsOpen(false), []);

	const handleIconClick = useCallback(() => {
		setShouldRenderEditSpaceLogoDialog(true);
		onOpen();
	}, [onOpen]);

	const renderEditSpaceLogoDialog = useCallback(() => {
		setShouldRenderEditSpaceLogoDialog(true);
	}, []);

	const hasEmoji = Boolean(emoji);
	const hasIcon = hasEmoji || Boolean(spaceIconPath);

	if (!hasIcon) {
		return null;
	}

	const iconContent = hasEmoji ? (
		<LazyEmojiComponentLoader
			emoji={emoji}
			context="spaceHeader"
			height={EMOJI_SIZE_LARGE}
			renderResourcedEmoji
			isPageTitleComponent
		/>
	) : (
		<img
			css={styles.spaceIcon}
			width={EMOJI_SIZE_LARGE}
			height={EMOJI_SIZE_LARGE}
			src={`${CONTEXT_PATH}${spaceIconPath}`}
			alt=""
			data-testid="smart-space-overview-logo-icon"
		/>
	);

	if (!isAdmin || isPersonalSpace) {
		return (
			<span css={styles.logoRoot} data-testid="smart-space-overview-logo">
				{iconContent}
			</span>
		);
	}

	return (
		<span css={styles.logoRoot} data-testid="smart-space-overview-logo">
			<Tooltip content={intl.formatMessage(i18n.editSpaceLogoTooltip)}>
				<button
					css={styles.spaceIconContainer}
					onClick={handleIconClick}
					onMouseEnter={() => {
						setIsHovered(true);
						renderEditSpaceLogoDialog();
					}}
					onMouseLeave={() => setIsHovered(false)}
					onFocus={() => {
						setIsHovered(true);
						renderEditSpaceLogoDialog();
					}}
					onBlur={() => setIsHovered(false)}
					aria-label={intl.formatMessage(i18n.editSpaceLogoTooltip)}
					data-testid="smart-space-overview-logo-edit-button"
					type="button"
				>
					<span css={styles.iconWrapper}>
						{iconContent}
						<div css={[styles.editIconOverlay, isHovered && styles.editIconOverlayVisible]}>
							<EditIcon label="" color={token('color.icon.inverse')} />
						</div>
					</span>
				</button>
			</Tooltip>
			{shouldRenderEditSpaceLogoDialog && (
				<EditSpaceLogo
					spaceKey={spaceKey}
					isOpen={isOpen}
					onOpen={onOpen}
					onClose={onClose}
					showSpaceNameField={false}
				/>
			)}
		</span>
	);
};
